import { defineStore, acceptHMRUpdate } from "pinia";
import { ref, shallowRef, watch } from "vue";
import { useEchoNest } from "../composables/audio";
import { useQueue } from "./queue";
import { useSources } from "./sources";
import { AudioSource } from "@wearesage/shared";
import type { QueueTrack } from "./queue";
import { adaptSpotifyTrack } from "../composables/audio/useTrackAdapter";

export const useSpotify = defineStore("spotify", () => {
  const queue = useQueue();
  const sources = useSources();
  const analysisData = shallowRef<any>();
  const { stream, volume, progress, position, track, playing, latency } = useEchoNest(analysisData);
  const fetchInterval = ref();

  const accessToken = ref<string | null>(localStorage.getItem("spotify_access_token"));
  const refreshToken = ref<string | null>(localStorage.getItem("spotify_refresh_token"));

  function setTokens(access: string, refresh: string) {
    accessToken.value = access;
    refreshToken.value = refresh;
    localStorage.setItem("spotify_access_token", access);
    localStorage.setItem("spotify_refresh_token", refresh);
  }

  function clearTokens() {
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem("spotify_access_token");
    localStorage.removeItem("spotify_refresh_token");
  }

  watch(progress, (val: number) => {
    if (val === 1 && sources.source === AudioSource.SPOTIFY) {
      getCurrentAnalysis();
    }
  });

  watch(track, (newTrack) => {
    if (!newTrack || sources.source !== AudioSource.SPOTIFY) return;

    const adaptedTrack = adaptSpotifyTrack(newTrack);
    if (!adaptedTrack) return;

    const queueTrack: QueueTrack = {
      id: adaptedTrack.id,
      source: adaptedTrack.source,
      sourceId: adaptedTrack.sourceId,
      title: adaptedTrack.title,
      artist: adaptedTrack.artist,
      album: adaptedTrack.album,
      duration: adaptedTrack.duration,
      artwork: adaptedTrack.artwork,
      rawData: adaptedTrack.rawData,
    };

    const currentQueueTrack = queue.currentTrack;
    if (!currentQueueTrack || currentQueueTrack.sourceId !== adaptedTrack.sourceId) {
      queue.setQueue([queueTrack], 0);
      queue.queue.isPlaying = playing.value;
    }
  });

  watch(playing, (isPlaying) => {
    if (sources.source === AudioSource.SPOTIFY && queue.currentTrack?.source === AudioSource.SPOTIFY) {
      queue.queue.isPlaying = isPlaying;
    }
  });

  async function refreshAccessToken() {
    if (!refreshToken.value) {
      console.error("Spotify: No refresh token available.");
      getSpotifyTokens();
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API}/api/spotify/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshToken.value }),
      });

      if (!response.ok) throw new Error("Failed to refresh token");

      const data = await response.json();
      setTokens(data.access_token, refreshToken.value);
    } catch (error) {
      console.error("Spotify: Failed to refresh access token:", error);
      clearTokens();
      getSpotifyTokens();
    }
  }

  function getSpotifyTokens() {
    if (!accessToken.value) {
      const currentUrl = window.location.href.split("?")[0];
      document.location.href = `${import.meta.env.VITE_API}/api/spotify/auth?returnUrl=${currentUrl}`;
    }
  }

  async function getCurrentAnalysis() {
    if (!accessToken.value) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API}/api/spotify/now-playing`, {
        headers: { Authorization: `Bearer ${accessToken.value}` },
      });

      if (response.status === 204) {
        analysisData.value = null;
        return;
      }

      if (response.status === 401) {
        await refreshAccessToken();
        await getCurrentAnalysis();
        return;
      }

      if (!response.ok) throw new Error(`API responded with status ${response.status}`);

      analysisData.value = await response.json();
    } catch (error) {
      console.error("Failed to get current analysis:", error);
    }
  }

  function stopInterval() {
    clearInterval(fetchInterval.value);
  }

  function startInterval() {
    stopInterval();
    fetchInterval.value = setInterval(async () => {
      await getCurrentAnalysis();
      if (analysisData.value) stopInterval();
    }, 5000);
  }

  function initialize() {
    const urlParams = new URLSearchParams(window.location.search);
    const access = urlParams.get("spotify_access_token");
    const refresh = urlParams.get("spotify_refresh_token");

    if (access && refresh) {
      setTokens(access, refresh);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (accessToken.value) {
      getCurrentAnalysis();
      if (!analysisData.value) startInterval();
    }
  }

  // Initialize on store creation to capture tokens from URL
  initialize();

  function reset() {
    stopInterval();
    analysisData.value = null;
  }

  return {
    track,
    analysisData,
    playing,
    stream,
    volume,
    position,
    progress,
    getSpotifyTokens,
    initialize,
    reset,
    latency,
    accessToken,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSpotify, import.meta.hot));
}
